import { useEffect, useState } from "react";
import { Button } from "../components/Button";
import { TextInput } from "../components/TextInput"
import { Title } from "../components/Title";
import { ValidationResponse } from "../components/ValidationResponse";
import { Dropdown } from "../components/Dropdown";
import { useModel } from "../hooks/useModel";
import { useSynchronousState } from "../hooks/useSynchronousState";
import { getUserDetailsSuccessType, getUserDetailsErrorType } from "../../../APIDocs/apiTypes";
import { useGetUserInfoQuery } from "../hooks/useJbpQuery";
import { Logger } from "../Logger";
import { Model } from "../Model";
import { Typography } from "../components/Typography";
import { Observable, ObservableEvents } from "../Observable";
import { useCanAccess, useStoreUserPermissions } from "../hooks/useUserPermissions";
import { useNavigate } from "react-router-dom";
import { useNav } from "../hooks/useNav";

export const Search = () => {

    const [searchDisabled, setSearchDisabled] = useState(true);
    const [dataErrorOccurred, setDataErrorOccurred] = useState(false)
    const mpan = useSynchronousState<string | undefined>(undefined)
    const selectedServiceLine = useSynchronousState<string | undefined>(undefined)
    const model = useModel(true);
    const nav = useNavigate();

    const onUserVerification = (userData: getUserDetailsSuccessType) => {
        useStoreUserPermissions(userData.permissions);
        Model.GetModel().Set("viewModel.fetchedData.UserTypeForCurrentUser", userData.userType);
        Model.GetModel().Set("viewModel.fetchedData.ValidMPIDSForCurrentUser", userData.mpid);
        Model.GetModel().Set("viewModel.fetchedData.UserRoleForCurrentUser", userData.role);
        Model.GetModel().Set("viewModel.fetchedData.UserPermissionsForCurrentUser", JSON.stringify(userData.permissions));
        const stringListOfMpids = userData.mpid.toString();
        sessionStorage.removeItem("userMPID");
        sessionStorage.setItem("listOfMpids", stringListOfMpids);
        Observable.GetObservable().emit(ObservableEvents.UserInfoLoaded, userData.user);
    }



    const onUserVerificationError = (err: getUserDetailsErrorType) => {
        Logger.Log(`Error occurred validating the user details -> ${err?.msg}`)
        setDataErrorOccurred(true);
    }
    const { isLoading: userDataLoading, data } = useGetUserInfoQuery<getUserDetailsSuccessType, getUserDetailsErrorType>(true, onUserVerification, onUserVerificationError)


    useEffect(() => {
        if(data) {
            handleQuickLink();
        }
    }, [data]);



    type serviceLine = {
        code: string,
        description: string,
        contractType: string,
        active: boolean | undefined
    }

    const contractTypesWhichRequireContractSelection = ['NHH', 'MSS', 'MSA'];

    // We originally defined NHH as
    const serviceLines: Array<serviceLine> = [
        {
            code: "HH",
            description: "Half Hourly",
            contractType: "8",
            active: useCanAccess('canUseHHServiceLine')
        },
        {
            code: "NHH",
            description: "Sub 100",
            contractType: "2",
            active: useCanAccess('canUseNHHServiceLine')
        },
        {
            code: "SUBMETER",
            description: "Sub Metering",
            contractType: "4",
            active: useCanAccess('canUseSubmeterServiceLine')
        },
        {
            code: "MSS",
            description: "Metering Service Smart",
            contractType: "111",
            active: useCanAccess('canUseMSSServiceLine')
        },
        {
            code: "MSA",
            description: "Metering Service Advanced",
            contractType: "112",
            active: useCanAccess('canUseMSAServiceLine')
        },
        {
            code: "DNO",
            description: "DNO",
            contractType: "100",
            active: useCanAccess('canUseDNOServiceLine')
        },
        {
            code: "BNO",
            description: "BNO",
            contractType: "101",
            active: useCanAccess('canUseBNOServiceLine')
        }
    ]

    const filteredServiceLines = serviceLines.filter(sl => sl.active === true);

    const onServiceLineSelected = (index: number): ValidationResponse | undefined => {
        selectedServiceLine.set(filteredServiceLines[index].code);
        model.Set("model.selectedContractType", serviceLines[index].contractType);
        setDisabledButtonFlag();
        return { isValid: true }
    }

    const searchClicked = async () => {
        const serviceLine = selectedServiceLine.get();
        const shouldSelectContractForServiceLine = serviceLine ? contractTypesWhichRequireContractSelection.includes(serviceLine) : false;
        if(useCanAccess('canViewContractSelect') === true && shouldSelectContractForServiceLine) {
            useNav(`/contract/${mpan.get()}/${serviceLine}`, nav);
            return;
        }

        sessionStorage.removeItem('lastAttemptedUrl');
        useNav(`/site/${mpan.get()}/${selectedServiceLine.get()}`, nav);
    }


    const handleQuickLink = () => {
        const url = sessionStorage.getItem('lastAttemptedUrl');
        if(url == null) return;

        const parts = url.split('/');
        const contractType = parts.pop();
        const mpanFromReferrer = parts.pop()?.split('-')[1];

        if(mpanFromReferrer == null || contractType == null) {
            console.warn("Invalid URL format for quick link");
            return;
        }

        if(contractType === 'NHH') {
            onMpanBlur(mpanFromReferrer, true);
        } else {
            onMpanBlur(mpanFromReferrer, false);
        }

        const index = filteredServiceLines.findIndex(sl => sl.code === contractType);
        if(index !== -1) {
            onServiceLineSelected(index);
            searchClicked();
        }
    }


    model.Set("model.dataFieldsSource.availableServiceLines", filteredServiceLines.map(sl => sl.description))

    const setDisabledButtonFlag = () => {
        const searchButtonShouldBeDisabled = mpan.get() == undefined || selectedServiceLine.get() == undefined
        setSearchDisabled(searchButtonShouldBeDisabled)
    }

    const onMpanBlur = (value: string, shouldAllowContractTypeInMPAN = false): ValidationResponse | undefined => {
        const mpanRegex = /^[0-9]{13}$/;

        const mpanWithContractAppend = /^[0-9]{1,3}-[0-9]{13}$/;

        if(mpanRegex.test(value.trim())) {
            mpan.set(value.trim());
            setDisabledButtonFlag();
            return { isValid: true };
        }
        if(shouldAllowContractTypeInMPAN && mpanWithContractAppend.test(value.trim())) {
            mpan.set(value.trim());
            setDisabledButtonFlag();
            return { isValid: true };
        }
        else {
            mpan.set(undefined);
            setDisabledButtonFlag();
            return { isValid: false, errorMessage: "The MPAN is invalid, it should contain 13 digits" };
        }
    }

    return (
        <div id="jbp-search-screen">
            <div className='w-1/2 inline-block align-top'>
                <div className='ml-10'>
                    <Title className="mt-7" text="Welcome to the Stark Job Booking Portal" />

                    {userDataLoading ? <Typography content="Please wait whilst we verify your user credentials" /> : <span></span>}
                    {dataErrorOccurred ? <Typography content="We could not validate your user credentials, please contact support." /> : <span></span>}

                    {!data ? <span></span> :
                        <div>
                            <TextInput title="MPAN" placeholder="Enter your site MPAN" onBlur={onMpanBlur} />
                            <Dropdown title="Service Line" mandatory={false} placeholder="Please select the service line" elementsDataSource="model.dataFieldsSource.availableServiceLines" onSelect={onServiceLineSelected} modelPath="model.selectedServiceLine" />
                            <Button primary={true} text="Search" icon={true} disabled={searchDisabled} onClick={searchClicked} />
                        </div>
                    }
                </div>
            </div>
            <div className="inline-block w-1/2 h-screen bg-center bg-origin-content bg-cover" style={{ backgroundImage: "url(/searchImagery.png)" }}></div>
        </div>
    )
}
